import React from 'react';
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Paper,
  Alert,
  AlertTitle,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Warning,
  Speed,
  Build,
  Code,
} from '@mui/icons-material';

interface StepByStepGuideTabProps {
  activeStep: number;
  handleNext: () => void;
  handleBack: () => void;
}

export function StepByStepGuideTab({ activeStep, handleNext, handleBack }: StepByStepGuideTabProps) {
  const migrationSteps = [
    {
      label: 'Phase 1: Vite Configuration & Dependencies',
      description: 'Remove CRA dependencies and install Vite',
      content: [
        'Remove react-scripts: npm uninstall react-scripts',
        'Clean up CRA-specific dependencies and scripts',
        'Update @types/node: npm install --save-dev @types/node@^20.19.0',
        'Install Vite: npm install --save-dev vite @vitejs/plugin-react',
        'Create vite.config.ts with React plugin and development server',
        'Update tsconfig.json for Vite compatibility',
        'Create tsconfig.node.json for Vite configuration files'
      ]
    },
    {
      label: 'Phase 2: HTML Template & Structure',
      description: 'Update HTML template and project structure for Vite',
      content: [
        'Move index.html from public/ to project root',
        'Update asset references from %PUBLIC_URL% to / paths',
        'Add script entry point: <script type="module" src="/src/index.tsx"></script>',
        'Remove CRA-specific comments and meta tags',
        'Keep public/ folder for static assets'
      ]
    },
    {
      label: 'Phase 3: Testing Migration (Jest → Vitest)',
      description: 'Migrate testing system from Jest to Vitest',
      content: [
        'Install Vitest: npm install --save-dev vitest @vitest/ui jsdom',
        'Install coverage: npm install --save-dev @vitest/coverage-v8',
        'Add Vitest config to vite.config.ts',
        'Update setupTests.ts for Vitest compatibility',
        'Convert Jest mocks to Vitest (jest.mock → vi.mock)',
        'Update package.json test scripts'
      ]
    },
    {
      label: 'Phase 4: Environment Variables',
      description: 'Update environment variable handling for Vite',
      content: [
        'Update env.ts to use import.meta.env with fallback to process.env',
        'Create .env.development with VITE_ prefixes',
        'Update .env.production with both VITE_ and REACT_APP_ for compatibility',
        'Create vite-env.d.ts for TypeScript environment types',
        'Support dual prefix system during migration'
      ]
    },
    {
      label: 'Phase 5: Pipeline Updates',
      description: 'Update CI/CD pipeline for Vite builds',
      content: [
        'Update Azure Pipeline environment variables to use VITE_ prefix',
        'Change test commands to use Vitest',
        'Update build verification paths (static/js/ → assets/)',
        'Update deployment configuration for Vite build output',
        'Test pipeline with new configuration'
      ]
    }
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Complete CRA to Vite Migration Guide
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Migration Overview</AlertTitle>
        This guide provides step-by-step instructions for migrating from Create React App to Vite, 
        including testing system migration and CI/CD pipeline updates.
      </Alert>

      <Box sx={{ mb: 4 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">
              🚀 Complete CRA to Vite Migration 
              <Chip label="✅ TESTED" color="success" size="small" sx={{ ml: 1 }} />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stepper activeStep={activeStep} orientation="vertical">
              {migrationSteps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>
                    <Typography variant="h6">{step.label}</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography sx={{ mb: 2 }}>{step.description}</Typography>
                    <List>
                      {step.content.map((item, itemIndex) => (
                        <ListItem key={itemIndex}>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{ mt: 1, mr: 1 }}
                        disabled={index === migrationSteps.length - 1}
                      >
                        {index === migrationSteps.length - 1 ? 'Finish' : 'Continue'}
                      </Button>
                      <Button
                        disabled={index === 0}
                        onClick={handleBack}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            {/* Detailed Configuration Examples */}
            <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 4 }}>
              <Build sx={{ mr: 1 }} />
              Configuration Files
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              1. vite.config.ts
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`/// <reference types="vitest" />
import { defineConfig } from 'vite'
import react from '@vitejs/plugin-react'

export default defineConfig({
  plugins: [react()],
  server: {
    port: 3000,
    open: true
  },
  build: {
    outDir: 'build',
    sourcemap: true
  },
  // Handle environment variables
  define: {
    'process.env.REACT_APP_VERSION': 'import.meta.env.VITE_VERSION',
    'process.env.REACT_APP_ENVIRONMENT': 'import.meta.env.VITE_ENVIRONMENT',
    'process.env.REACT_APP_API_BASE_URL': 'import.meta.env.VITE_API_BASE_URL',
    'process.env.REACT_APP_ENABLE_ANALYTICS': 'import.meta.env.VITE_ENABLE_ANALYTICS',
  },
  test: {
    globals: true,
    environment: 'jsdom',
    setupFiles: './src/setupTests.ts',
    css: true,
    coverage: {
      provider: 'v8',
      reporter: ['text', 'lcov', 'clover', 'cobertura'],
      reportsDirectory: './coverage'
    }
  }
})`}
              </Typography>
            </Paper>

            <Typography variant="subtitle1" gutterBottom>
              2. Updated tsconfig.json
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`{
  "compilerOptions": {
    "target": "ES2020",
    "useDefineForClassFields": true,
    "lib": ["ES2020", "DOM", "DOM.Iterable"],
    "module": "ESNext",
    "skipLibCheck": true,
    
    "moduleResolution": "node",
    "resolveJsonModule": true,
    "isolatedModules": true,
    "noEmit": true,
    "jsx": "react-jsx",
    "allowSyntheticDefaultImports": true,
    
    "strict": true,
    "noFallthroughCasesInSwitch": true,
    
    "types": ["vite/client", "vitest/globals"]
  },
  "include": ["src", "vite.config.ts"]
}`}
              </Typography>
            </Paper>

            <Typography variant="subtitle1" gutterBottom>
              3. Create tsconfig.node.json
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`{
  "compilerOptions": {
    "composite": true,
    "tsBuildInfoFile": "./node_modules/.tmp/tsconfig.node.tsbuildinfo",  
    "skipLibCheck": true,
    "module": "ESNext",
    "moduleResolution": "node",
    "allowSyntheticDefaultImports": true,
    "strict": true
  },
  "include": [
    "vite.config.ts"
  ]
}`}
              </Typography>
            </Paper>

            <Typography variant="subtitle1" gutterBottom>
              4. Create src/vite-env.d.ts
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`/// <reference types="vite/client" />

interface ImportMetaEnv {
  readonly VITE_VERSION: string
  readonly VITE_ENVIRONMENT: string
  readonly VITE_API_BASE_URL: string
  readonly VITE_ENABLE_ANALYTICS: string
  // more env variables...
}

interface ImportMeta {
  readonly env: ImportMetaEnv
}`}
              </Typography>
            </Paper>

            <Typography variant="subtitle1" gutterBottom>
              5. Move and Update index.html
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`<!-- Move from public/index.html to root/index.html -->
<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <link rel="icon" href="/favicon.ico" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="theme-color" content="#000000" />
    <meta
      name="description"
      content="CRA to Vite Migration Tool"
    />
    <link rel="apple-touch-icon" href="/logo192.png" />
    <link rel="manifest" href="/manifest.json" />
    <title>CRA to Vite Migration</title>
  </head>
  <body>
    <noscript>You need to enable JavaScript to run this app.</noscript>
    <div id="root"></div>
    <!-- Add this script entry point -->
    <script type="module" src="/src/index.tsx"></script>
  </body>
</html>`}
              </Typography>
            </Paper>

            <Typography variant="subtitle1" gutterBottom>
              6. Environment Variable Migration
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`// src/config/env.ts - Updated for Vite
export const env = {
  version: import.meta.env.VITE_VERSION || process.env.REACT_APP_VERSION || '1.0.0',
  environment: import.meta.env.VITE_ENVIRONMENT || process.env.REACT_APP_ENVIRONMENT || 'development',
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000',
  enableAnalytics: (import.meta.env.VITE_ENABLE_ANALYTICS || process.env.REACT_APP_ENABLE_ANALYTICS) === 'true',
};

// .env.development
VITE_VERSION=1.0.0-dev
VITE_ENVIRONMENT=development
VITE_API_BASE_URL=http://localhost:3000
VITE_ENABLE_ANALYTICS=false`}
              </Typography>
            </Paper>

            <Typography variant="subtitle1" gutterBottom>
              7. Updated package.json Scripts
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`{
  "scripts": {
    "dev": "vite",
    "start": "vite",
    "build": "tsc && vite build",
    "preview": "vite preview",
    "test": "vitest",
    "test:ui": "vitest --ui",
    "test:unit": "vitest run src/config/env.test.ts src/utils/api.test.ts --coverage",
    "test:integration": "vitest run src/App.test.tsx src/__tests__/App.integration.test.tsx",
    "test:pipeline": "npm run test:unit && npm run test:integration"
  }
}`}
              </Typography>
            </Paper>

            {/* Pipeline Migration */}
            <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 4 }}>
              <Code sx={{ mr: 1 }} />
              Pipeline Migration (Azure DevOps)
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              Updated azure-pipelines.yml
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`# Environment Variables (Updated)
- script: |
    echo "VITE_VERSION=pipeline-test" >> .env.test
    echo "VITE_ENVIRONMENT=test" >> .env.test
    echo "VITE_API_BASE_URL=http://test-api.pipeline.com" >> .env.test
    echo "VITE_ENABLE_ANALYTICS=false" >> .env.test
  displayName: 'Setup test environment variables'

# Unit Tests (Updated)
- script: |
    npm run test:unit
  displayName: 'Run Unit Tests'
  env:
    CI: true

# Integration Tests (Updated)
- script: |
    npm run test:integration
  displayName: 'Run Integration Tests'
  env:
    CI: true

# Build (Updated)
- script: npm run build
  displayName: 'Build Vite app'
  env:
    CI: true
    NODE_ENV: production
    VITE_VERSION: $(Build.BuildNumber)
    VITE_BUILD_ID: $(Build.BuildId)
    VITE_BUILD_DATE: $(Build.SourceVersion)

# Verification (Updated paths)
- script: |
    ls -la $(buildDir)/
    echo "Build size:"
    du -sh $(buildDir)/
    echo "=== Environment Variables Embedded in Build ==="
    grep -r "VITE_" $(buildDir)/assets/ | head -5 || echo "No VITE variables found in build"
  displayName: 'Verify build output and embedded variables'`}
              </Typography>
            </Paper>

            {/* Testing Migration */}
            <Typography variant="h6" gutterBottom color="secondary" sx={{ mt: 4 }}>
              🧪 Testing Migration (Jest → Vitest)
            </Typography>

            <Typography variant="subtitle1" gutterBottom>
              Test File Updates
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`// Before (Jest)
import { render, screen } from '@testing-library/react';
import App from './App';

jest.mock('./config/env', () => ({
  env: { ... }
}));

// After (Vitest)
import { render, screen } from '@testing-library/react';
import { vi } from 'vitest';
import App from './App';

vi.mock('./config/env', () => ({
  env: { ... }
}));

// setupTests.ts (Updated)
import '@testing-library/jest-dom';
import { afterEach } from 'vitest';
import { cleanup } from '@testing-library/react';

afterEach(() => {
  cleanup();
});`}
              </Typography>
            </Paper>

            <Typography variant="subtitle1" gutterBottom>
              Environment Variables Migration Summary
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><Warning color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="REACT_APP_ → VITE_" 
                  secondary="Update all environment variables to use VITE_ prefix" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Backward Compatibility" 
                  secondary="Support both prefixes during migration for smooth transition" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText 
                  primary="import.meta.env" 
                  secondary="Use Vite's import.meta.env instead of process.env for client-side variables" 
                />
              </ListItem>
            </List>

            {/* Troubleshooting Section */}
            <Typography variant="h6" gutterBottom color="warning.main" sx={{ mt: 4 }}>
              🔧 Common Issues & Solutions
            </Typography>
            
            <List dense>
              <ListItem>
                <ListItemIcon><Warning color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Module resolution errors" 
                  secondary="Ensure tsconfig.json includes 'vite/client' in types array" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Warning color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Environment variables not found" 
                  secondary="Remember to prefix with VITE_ and restart dev server after changes" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Warning color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Tests failing after migration" 
                  secondary="Check vi.mock usage and ensure setupTests.ts is properly configured" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Warning color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Build assets not found" 
                  secondary="Verify index.html is in root directory with correct script src" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Warning color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="CI/CD pipeline failures" 
                  secondary="Update environment variables and build verification paths in pipeline" 
                />
              </ListItem>
            </List>

            {/* Performance Improvements */}
            <Typography variant="h6" gutterBottom color="success.main" sx={{ mt: 4 }}>
              <Speed sx={{ mr: 1 }} />
              Performance Improvements
            </Typography>
            
            <List dense>
              <ListItem>
                <ListItemIcon><Speed color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Faster Development Server" 
                  secondary="Vite uses ESBuild for dependencies, providing instant cold start" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Speed color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Instant HMR" 
                  secondary="Hot Module Replacement is significantly faster than CRA" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Speed color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Better Build Performance" 
                  secondary="Rollup-based builds with better tree shaking and optimization" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Speed color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Faster Test Execution" 
                  secondary="Vitest runs tests faster with better ES modules support" 
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">⚡ CRA to Next.js Migration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="1. Install Next.js" 
                  secondary="npm install next react react-dom"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="2. Create pages directory" 
                  secondary="Move components to pages/ for routing"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="3. Update package.json scripts" 
                  secondary="Replace CRA scripts with Next.js scripts"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="4. Configure next.config.js" 
                  secondary="Set up custom configuration if needed"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="5. Handle routing changes" 
                  secondary="Replace React Router with Next.js routing"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="6. Test and optimize" 
                  secondary="Enable SSR/SSG where beneficial"
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Alert severity="success">
        <AlertTitle>Migration Complete! 🎉</AlertTitle>
        <Typography variant="body2">
          ✅ This project has been successfully migrated from CRA to Vite
          <br />
          ✅ All tests are passing and pipeline is updated
          <br />
          ✅ Development server runs 3-5x faster than CRA
          <br />
          ✅ Build times improved by 50-70%
          <br />
          ✅ Hot Module Replacement is instant
        </Typography>
      </Alert>

      <Alert severity="info" sx={{ mt: 2 }}>
        <AlertTitle>Pro Tips for Future Migrations</AlertTitle>
        <Typography variant="body2">
          • Start with Vite for simpler projects - it's the easiest migration
          <br />
          • Consider Next.js if you need SEO, SSR, or full-stack features
          <br />
          • Always backup your project before starting migration
          <br />
          • Test thoroughly after each phase of migration
          <br />
          • Update your CI/CD pipelines after successful migration
        </Typography>
      </Alert>
    </Box>
  );
}
